import SvgIcon, { SvgIconProps } from '@mui/material/SvgIcon'
import AccessTime from '@mui/icons-material/AccessTime'
import Add from '@mui/icons-material/Add'
import AddCircleOutline from '@mui/icons-material/AddCircleOutline'
import ArrowBack from '@mui/icons-material/ArrowBack'
import ArrowDownward from '@mui/icons-material/ArrowDownward'
import ArrowForward from '@mui/icons-material/ArrowForward'
import Check from '@mui/icons-material/Check'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Close from '@mui/icons-material/Close'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import EditNote from '@mui/icons-material/EditNote'
import EditOutlined from '@mui/icons-material/EditOutlined'
import FiberManualRecord from '@mui/icons-material/FiberManualRecord'
import FileCopyOutlined from '@mui/icons-material/FileCopyOutlined'
import MoreHoriz from '@mui/icons-material/MoreHoriz'
import PersonOutline from '@mui/icons-material/PersonOutline'
import Refresh from '@mui/icons-material/Refresh'
import Remove from '@mui/icons-material/Remove'
import SwapVert from '@mui/icons-material/SwapVert'
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined'
import {
	T3CostCaptureIcon as Ds2T3CostCaptureIcon,
	T3FleetIcon as Ds2T3FleetIcon,
} from '@equipmentshare/ds2-icons'

/**
 * Icon map for the DS2 → MUI migration (sc-538983).
 *
 * Every icon the app imports from `@equipmentshare/ds2-icons` is re-exported
 * here under its existing name, mapped to the `@mui/icons-material` glyph
 * approved in the design mapping decisions. Call sites can swap
 * `@equipmentshare/ds2-icons` for `components/icons` without touching JSX.
 */
export { default as AddCircleLineIcon } from '@mui/icons-material/AddCircleOutline'
export { default as AddLineIcon } from '@mui/icons-material/Add'
export { default as ArrowDownSLineIcon } from '@mui/icons-material/KeyboardArrowDown'
export { default as ArrowUpSLineIcon } from '@mui/icons-material/KeyboardArrowUp'
export { default as BuildingLineIcon } from '@mui/icons-material/BusinessOutlined'
export { default as CloseLineIcon } from '@mui/icons-material/Close'
export { default as DeleteBin7LineIcon } from '@mui/icons-material/DeleteOutline'
export { default as FileCopyLineIcon } from '@mui/icons-material/ContentCopy'
export { default as FileTextLineIcon } from '@mui/icons-material/DescriptionOutlined'
export { default as HomeLineIcon } from '@mui/icons-material/HomeOutlined'
export { default as More2FillIcon } from '@mui/icons-material/MoreVert'
export { default as RefreshLineIcon } from '@mui/icons-material/Refresh'
export { default as SendPlane2LineIcon } from '@mui/icons-material/SendOutlined'
export { default as ShareBoxFillIcon } from '@mui/icons-material/OpenInNew'
export { default as StarLineIcon } from '@mui/icons-material/StarBorder'
export { default as TruckLineIcon } from '@mui/icons-material/LocalShippingOutlined'

/**
 * The T3 brand icons have no MUI equivalent. The existing ds2 SVGs are
 * wrapped in MUI's SvgIcon so they accept the same sizing/color props as
 * every other icon in this map. `inheritViewBox` is required — the T3
 * glyphs are not drawn on MUI's default 0 0 24 24 viewBox.
 */
export const T3CostCaptureIcon = ( props: SvgIconProps ) => (
	<SvgIcon component={Ds2T3CostCaptureIcon} inheritViewBox {...props} />
)

export const T3FleetIcon = ( props: SvgIconProps ) => (
	<SvgIcon component={Ds2T3FleetIcon} inheritViewBox {...props} />
)

/**
 * EUI built-in string icons (`iconType="trash"`, `icon="cross"`,
 * `<EuiIcon type="check" />`, …) mapped to their approved MUI glyphs,
 * keyed by the exact EUI string name. Use this when migrating a call site
 * off an EUI component so the glyph choice comes from the approved map
 * rather than being re-derived per story.
 */
export const euiIconTypeMap = {
	alert: WarningAmberOutlined,
	arrowDown: ArrowDownward,
	arrowLeft: ArrowBack,
	arrowRight: ArrowForward,
	boxesHorizontal: MoreHoriz,
	check: Check,
	checkInCircleFilled: CheckCircle,
	clock: AccessTime,
	cross: Close,
	// Added for the fulfillment-order notes action; not in the original design
	// table, but the glyph choice is design-approved.
	documentEdit: EditNote,
	documents: FileCopyOutlined,
	dot: FiberManualRecord,
	// EUI's merge glyph only appears as the merge forms' swap button, rotated into up/down arrows.
	merge: SwapVert,
	minus: Remove,
	pencil: EditOutlined,
	plus: Add,
	plusInCircle: AddCircleOutline,
	refresh: Refresh,
	trash: DeleteOutline,
	user: PersonOutline,
}
